export const getCSRFToken = (): string | null | undefined => {
  return document
    .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    ?.getAttribute('content');
};

const getHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    Accept: 'application/json',
  };

  const csrfToken = getCSRFToken();

  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  return headers;
};

// Extract plain-object headers from RequestInit options for merging
const extractHeaders = (options?: RequestInit): Record<string, string> => {
  if (!options?.headers) return {};
  if (options.headers instanceof Headers) {
    const result: Record<string, string> = {};
    options.headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
  if (Array.isArray(options.headers)) {
    return Object.fromEntries(options.headers);
  }
  return options.headers as Record<string, string>;
};

export interface UploadProgressEvent {
  loaded: number;
  total: number;
}

interface UploadWithProgressOptions {
  method?: string;
  body: FormData;
  onProgress?: (event: UploadProgressEvent) => void;
}

// fetch() exposes no upload-progress events, so uploads that need a progress
// bar go through XMLHttpRequest. Resolves with a standard Response so callers
// handle it exactly like a fetch result (`response.ok`, `response.json()`).
const uploadWithProgress = (
  route: string,
  { method = 'POST', body, onProgress }: UploadWithProgressOptions
): Promise<Response> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `/api/${API.version}${route}`);
    Object.entries(getHeaders() as Record<string, string>).forEach(
      ([key, value]) => xhr.setRequestHeader(key, value)
    );

    if (onProgress) {
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          onProgress({ loaded: event.loaded, total: event.total });
        }
      };
    }

    const networkError = () =>
      reject(new Error('Network error while uploading'));
    xhr.onerror = networkError;
    xhr.onabort = networkError;
    xhr.onload = () => {
      if (xhr.status === 0) {
        networkError();
        return;
      }
      // The Response constructor throws if a null-body status carries a body.
      const hasBody = ![204, 205, 304].includes(xhr.status);
      resolve(
        new Response(hasBody ? xhr.responseText : null, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: {
            'Content-Type':
              xhr.getResponseHeader('Content-Type') ?? 'application/json',
          },
        })
      );
    };

    xhr.send(body);
  });

export const API = {
  version: 'v1',
  authenticated: {
    fetch: async (route: string, options?: RequestInit) => {
      return fetch(`/api/${API.version}${route}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...getHeaders(),
          ...extractHeaders(options),
        },
      });
    },
    upload: async (route: string, options?: RequestInit) => {
      return fetch(`/api/${API.version}${route}`, {
        ...options,
        headers: {
          ...getHeaders(),
        },
        body: options?.body as FormData,
      });
    },
    uploadWithProgress,
    admin: {
      fetch: async (route: string, options?: RequestInit) => {
        return fetch(`/api/admin${route}`, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            ...getHeaders(),
          },
        });
      },
    },
  },
  fetch: async (route: string, options?: RequestInit) => {
    return fetch(`/api/${API.version}${route}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...getHeaders(),
      },
    });
  },
};
